import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Save, UserCircle2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const BASE_INITIAL_FORM = Object.freeze({
  accountName: "",
  firstName: "",
  lastName: "",
  displayName: "",
  jobTitle: "",
  bio: "",
  phone: "",
  website: "",
});

export const ProfileSettings = () => {
  const { username } = useAuth();
  const initialValues = useMemo(
    () => ({
      ...BASE_INITIAL_FORM,
      accountName: username || "",
    }),
    [username]
  );

  const [formValues, setFormValues] = useState(initialValues);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const isDirty = useMemo(
    () => Object.keys(initialValues).some((key) => formValues[key] !== initialValues[key]),
    [formValues, initialValues]
  );

  useEffect(() => {
    setFormValues(initialValues);
  }, [initialValues]);

  const handleInputChange = (field) => (event) => {
    const { value } = event.target;
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      await new Promise((resolve) => setTimeout(resolve, 600));
      setMessage({
        kind: "success",
        text: "Profile saved locally. Connect this form to your API to persist changes.",
      });
    } catch (error) {
      setMessage({ kind: "error", text: error.message || "Unable to save profile settings." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      <header className="flex items-start gap-4">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-slate-900 text-white shadow-lg shadow-slate-900/20">
          <UserCircle2 size={36} />
        </div>
        <div className="flex flex-col gap-1">
          <h2 className="text-2xl font-semibold text-slate-900">Profile settings</h2>
          <p className="text-sm text-slate-500">
            Update the profile details teammates see across dashboards and exports.
          </p>
        </div>
      </header>

      {message && (
        <div
          className={`rounded-2xl border px-4 py-3 text-sm ${
            message.kind === "error"
              ? "border-rose-200 bg-rose-50/80 text-rose-700"
              : "border-emerald-200 bg-emerald-50/80 text-emerald-700"
          }`}
        >
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="space-y-6">
          <section className="rounded-3xl border border-white/30 bg-white/95 shadow-inner shadow-white/40">
            <div className="space-y-6 p-6">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Account details</h3>
                <p className="text-sm text-slate-500">
                  Control the core identity attached to this Allegro account.
                </p>
              </div>
              <label className="flex flex-col gap-2 text-sm text-slate-600">
                Account email
                <input
                  type="email"
                  value={formValues.accountName}
                  onChange={handleInputChange("accountName")}
                  placeholder="you@example.com"
                  className="rounded-2xl border border-slate-200 bg-white/80 px-4 py-2 text-slate-900 shadow-inner shadow-white/40 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300"
                />
              </label>
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Basic information</h3>
                <p className="text-sm text-slate-500">
                  Tell teammates who you are. These fields can show up on order exports.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <label className="flex flex-col gap-2 text-sm text-slate-600">
                  First name
                  <input
                    type="text"
                    value={formValues.firstName}
                    onChange={handleInputChange("firstName")}
                    placeholder="Jane"
                    className="rounded-2xl border border-slate-200 bg-white/80 px-4 py-2 text-slate-900 shadow-inner shadow-white/40 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300"
                  />
                </label>
                <label className="flex flex-col gap-2 text-sm text-slate-600">
                  Last name
                  <input
                    type="text"
                    value={formValues.lastName}
                    onChange={handleInputChange("lastName")}
                    placeholder="Doe"
                    className="rounded-2xl border border-slate-200 bg-white/80 px-4 py-2 text-slate-900 shadow-inner shadow-white/40 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300"
                  />
                </label>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <label className="flex flex-col gap-2 text-sm text-slate-600">
                  Display name
                  <input
                    type="text"
                    value={formValues.displayName}
                    onChange={handleInputChange("displayName")}
                    placeholder="Jane from Allegro"
                    className="rounded-2xl border border-slate-200 bg-white/80 px-4 py-2 text-slate-900 shadow-inner shadow-white/40 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300"
                  />
                </label>
                <label className="flex flex-col gap-2 text-sm text-slate-600">
                  Job title
                  <input
                    type="text"
                    value={formValues.jobTitle}
                    onChange={handleInputChange("jobTitle")}
                    placeholder="Marketplace manager"
                    className="rounded-2xl border border-slate-200 bg-white/80 px-4 py-2 text-slate-900 shadow-inner shadow-white/40 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300"
                  />
                </label>
              </div>

              <label className="flex flex-col gap-2 text-sm text-slate-600">
                Bio
                <textarea
                  rows={4}
                  value={formValues.bio}
                  onChange={handleInputChange("bio")}
                  placeholder="Share what you do or how teammates can reach you."
                  className="rounded-2xl border border-slate-200 bg-white/80 px-4 py-3 text-sm text-slate-900 shadow-inner shadow-white/40 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300"
                />
              </label>
            </div>
          </section>

          <section className="rounded-3xl border border-white/30 bg-white/95 shadow-inner shadow-white/40">
            <div className="space-y-6 p-6">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Contact</h3>
                <p className="text-sm text-slate-500">
                  Keep your contact methods up to date so notified teammates can respond quickly.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <label className="flex flex-col gap-2 text-sm text-slate-600">
                  Phone
                  <input
                    type="tel"
                    value={formValues.phone}
                    onChange={handleInputChange("phone")}
                    placeholder="+48 123 456 789"
                    className="rounded-2xl border border-slate-200 bg-white/80 px-4 py-2 text-slate-900 shadow-inner shadow-white/40 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300"
                  />
                </label>
                <label className="flex flex-col gap-2 text-sm text-slate-600">
                  Website or store link
                  <input
                    type="url"
                    value={formValues.website}
                    onChange={handleInputChange("website")}
                    placeholder="https://yourshop.example"
                    className="rounded-2xl border border-slate-200 bg-white/80 px-4 py-2 text-slate-900 shadow-inner shadow-white/40 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300"
                  />
                </label>
              </div>
            </div>
          </section>
        </div>

        <aside className="space-y-4">
          <div className="rounded-3xl border border-dashed border-slate-300/70 bg-white/70 p-6 text-sm text-slate-600 shadow-inner shadow-white/40">
            <h3 className="text-base font-semibold text-slate-900">Need to sync with the backend?</h3>
            <p className="mt-2">
              Wire this form to your profile endpoint and replace the simulated delay inside{" "}
              <code>ProfileSettings</code>. The UI is ready for optimistic updates and status states.
            </p>
          </div>

          <button
            type="submit"
            disabled={!isDirty || saving}
            className={`flex w-full items-center justify-center gap-2 rounded-2xl px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-slate-900/20 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 focus:ring-offset-white ${
              !isDirty || saving
                ? "cursor-not-allowed bg-slate-400/80"
                : "bg-slate-900 hover:bg-slate-800"
            }`}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {saving ? "Saving…" : "Save profile"}
          </button>
        </aside>
      </form>
    </div>
  );
};

export default ProfileSettings;
